import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs';

import { AuthFacade } from '../../../modules/auth/facades';
import { NavigationStateService, PermissionsService } from '../../services';
import { getGuidedTourDefinition } from '../registry/guided-tour.registry';
import { GuidedTourDefinition, GuidedTourId, GuidedTourStep, GuidedTourViewport } from '../models';
import { GuidedTourTargetResolverService } from './guided-tour-target-resolver.service';
import { OnboardingProgressApiService } from './onboarding-progress-api.service';

export const GUIDED_TOUR_MOBILE_QUERY = '(max-width: 47.9375rem)';
const PANEL_SETTLE_MS = 250;

export interface ActiveGuidedTour {
  definition: GuidedTourDefinition;
  steps: GuidedTourStep[];
  index: number;
  persist: boolean;
}

/**
 * Máquina de estados do tour em memória. A etapa atual vive só aqui (UI local);
 * a persistência acontece apenas ao iniciar, pular e concluir, e nunca bloqueia.
 * O motor destaca elementos: não executa ações operacionais.
 */
@Injectable({ providedIn: 'root' })
export class GuidedTourService {
  private readonly permissions = inject(PermissionsService);
  private readonly resolver = inject(GuidedTourTargetResolverService);
  private readonly api = inject(OnboardingProgressApiService);
  private readonly router = inject(Router);
  private readonly navigation = inject(NavigationStateService);
  private readonly authFacade = inject(AuthFacade);
  private readonly destroyRef = inject(DestroyRef);

  private readonly state = signal<ActiveGuidedTour | null>(null);
  private companyAccountId: string | null = null;
  private restoreFocusTo: HTMLElement | null = null;
  private navigationSnapshot: ReturnType<NavigationStateService['snapshot']> | null = null;
  private starting = false;

  readonly active = this.state.asReadonly();
  readonly isActive = computed(() => this.state() !== null);
  readonly currentStep = computed(() => {
    const tour = this.state();
    return tour ? (tour.steps[tour.index] ?? null) : null;
  });
  readonly stepNumber = computed(() => (this.state()?.index ?? 0) + 1);
  readonly totalSteps = computed(() => this.state()?.steps.length ?? 0);
  readonly isFirstStep = computed(() => (this.state()?.index ?? 0) === 0);
  readonly isLastStep = computed(() => {
    const tour = this.state();
    return !!tour && tour.index === tour.steps.length - 1;
  });

  constructor() {
    // Qualquer navegação ou troca de empresa encerra o tour sem persistir.
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.cancel());
    this.authFacade.company$?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((company) => {
      const current = company?.companyAccountId ?? null;
      if (this.state() && current !== this.companyAccountId) this.cancel();
    });
    this.authFacade.isAuthenticated$
      ?.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((authenticated) => {
        if (!authenticated) this.cancel();
      });
  }

  /** Retorna false quando não há nenhuma etapa aplicável (nada é exibido nem gravado). */
  async start(tourId: GuidedTourId, options: { persist?: boolean } = {}): Promise<boolean> {
    if (this.state() || this.starting) return false;
    const definition = getGuidedTourDefinition(tourId);
    if (!definition) return false;
    this.starting = true;
    try {
      const steps = await this.resolveSteps(definition);
      if (!steps || steps.length === 0 || this.state()) {
        this.restoreNavigation();
        return false;
      }
      this.companyAccountId = this.authFacade.currentCompany?.companyAccountId ?? null;
      this.restoreFocusTo =
        document.activeElement instanceof HTMLElement ? document.activeElement : null;
      const persist = options.persist ?? true;
      this.state.set({ definition, steps, index: 0, persist });
      if (persist) this.persist(definition, { status: 'in_progress', lastStep: steps[0].id });
      return true;
    } finally {
      this.starting = false;
    }
  }

  next(): void {
    const tour = this.state();
    if (!tour) return;
    if (tour.index >= tour.steps.length - 1) {
      this.finish();
      return;
    }
    this.state.set({ ...tour, index: tour.index + 1 });
  }

  back(): void {
    const tour = this.state();
    if (!tour || tour.index === 0) return;
    this.state.set({ ...tour, index: tour.index - 1 });
  }

  skip(): void {
    const tour = this.state();
    if (!tour) return;
    if (tour.persist) {
      this.persist(tour.definition, { status: 'dismissed', lastStep: tour.steps[tour.index].id });
    }
    this.end();
  }

  finish(): void {
    const tour = this.state();
    if (!tour) return;
    if (tour.persist) {
      this.persist(tour.definition, { status: 'completed', lastStep: tour.steps.at(-1)?.id });
    }
    this.end();
  }

  /** Encerra sem gravar nada (troca de rota, empresa, sessão ou destruição do host). */
  cancel(): void {
    this.end();
  }

  /** O alvo sumiu: remove a etapa e renumera; sem etapas restantes, conclui. */
  dropStep(stepId: string): void {
    const tour = this.state();
    if (!tour) return;
    const removedIndex = tour.steps.findIndex((step) => step.id === stepId);
    if (removedIndex === -1) return;
    const steps = tour.steps.filter((step) => step.id !== stepId);
    if (steps.length === 0) {
      this.end();
      return;
    }
    if (tour.steps[removedIndex].fallback === 'abort') {
      this.end();
      return;
    }
    let index = tour.index;
    if (removedIndex < index) index -= 1;
    if (index >= steps.length) {
      this.state.set({ ...tour, steps, index: steps.length - 1 });
      this.finish();
      return;
    }
    this.state.set({ ...tour, steps, index });
  }

  private async resolveSteps(definition: GuidedTourDefinition): Promise<GuidedTourStep[] | null> {
    const viewport: GuidedTourViewport = window.matchMedia(GUIDED_TOUR_MOBILE_QUERY).matches
      ? 'mobile'
      : 'desktop';
    const allowed = definition.steps.filter(
      (step) =>
        (!step.supportedViewports || step.supportedViewports.includes(viewport)) &&
        (!step.requiredAnyOf ||
          step.requiredAnyOf.some((req) => this.permissions.can(req.module, req.action)))
    );
    await this.prepareNavigation(allowed);
    const found = await Promise.all(
      allowed.map(async (step) => ({
        step,
        element: await this.resolver.waitFor(step.targetId)
      }))
    );
    if (found.some(({ step, element }) => !element && step.fallback === 'abort')) return null;
    return found.filter(({ element }) => !!element).map(({ step }) => step);
  }

  /** Abre o painel do menu para etapas ancoradas nele; o estado anterior é restaurado ao fim. */
  private async prepareNavigation(steps: GuidedTourStep[]): Promise<void> {
    const domain = steps.find((step) => step.prepare)?.prepare?.navigationDomain;
    if (!domain) return;
    this.navigationSnapshot = this.navigation.snapshot();
    this.navigation.openDomain(domain);
    // aguarda a animação do painel antes de medir os alvos
    await new Promise<void>((resolve) => setTimeout(resolve, PANEL_SETTLE_MS));
  }

  private restoreNavigation(): void {
    if (!this.navigationSnapshot) return;
    this.navigation.restore(this.navigationSnapshot);
    this.navigationSnapshot = null;
  }

  private persist(
    definition: GuidedTourDefinition,
    payload: { status: 'in_progress' | 'dismissed' | 'completed'; lastStep?: string }
  ): void {
    // Falha de progresso nunca interrompe o uso do sistema.
    this.api
      .updateTour(definition.id, { ...payload, version: definition.version })
      .subscribe({ error: () => undefined });
  }

  private end(): void {
    this.restoreNavigation();
    const focusTarget = this.restoreFocusTo;
    this.state.set(null);
    this.restoreFocusTo = null;
    this.companyAccountId = null;
    if (focusTarget?.isConnected) focusTarget.focus({ preventScroll: true });
  }
}
